import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { CiMenuFries } from "react-icons/ci";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useLanguage } from "context/LanguageContext";
import { TfiClose } from "react-icons/tfi";
import LanguageSelector from "components/LanguageSelector";
import ConatctUs from "pages/contactus/ContactUs";
import { Link as ScrollLink } from "react-scroll";
import appLogoScroll from "../../assets/images/LogoSection(2).png";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const currentpath = location.pathname + location?.hash;
  const dropdownRef = useRef(null);

  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownContent, setDropdownContent] = useState(null);
  const { lang } = useLanguage();

  const navLinks = useMemo(() => {
    return [
      { name: t("navbar.home"), to: "/" },
      { name: t("navbar.roles"), to: "/#roles", isScroll: true },
      { name: t("navbar.features"), to: "/#features", isScroll: true },
      { name: t("navbar.whyUs"), to: "/#whyus", isScroll: true },
      { name: t("navbar.contactUs"), to: "contactus" },
    ];
  }, [lang]);

  const toggledropdown = (dropdownType) => {
    setIsDropdownOpen(true);
    setDropdownContent(dropdownType);
  };

  const handleCloseDropdown = () => {
    setIsDropdownOpen(false);
    setDropdownContent(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (dropdownContent === "contactus") {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [dropdownContent === "contactus"]);

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const element = document.getElementById(location.hash.substring(1));
        if (element) {
          const elementPosition =
            element.getBoundingClientRect().top + window.scrollY;
          const offsetPosition = elementPosition - 60;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  }, [location]);

  return (
    <>
      <nav
        className={`sticky top-0 left-0 w-full z-[250] bg-white transition-all duration-300 ease-in-out ${
          isScrolled || isDropdownOpen ? "shadow-md pt-0" : "pt-[10px]"
        }`}
      >
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8"
        >
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="flex items-center w-[55%] sm:w-[35%] lg:w-[20%]"
              onClick={handleCloseDropdown}
            >
              <motion.div
                initial={false}
                animate={{
                  scale: isScrolled ? 1 : 1.1,
                  opacity: isScrolled ? 1 : 0.9,
                }}
                transition={{
                  type: "spring",
                  stiffness: 200,
                  damping: 15,
                  mass: 0.5,
                }}
                className={"w-[100%] h-[100%]"}
              >
                <img
                  src={appLogoScroll}
                  alt="logo"
                  className="w-full h-full object-contain"
                />
              </motion.div>
            </Link>

            <div className="hidden md:flex items-center space-x-[38px] md:space-x-[48px] xl:space-x-[58px]">
              {navLinks.map((link, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.3 }}
                >
                  <Link
                    to={link.to !== "contactus" && link.to}
                    className={`pb-1 text-[18px] 2xl:text-[20px] leading-[16.94px] relative after:content-[''] after:absolute after:left-1/2 after:bottom-0 after:w-0 after:h-[2px] ${
                      currentpath === link.to ? "after:w-4/5" : ""
                    } after:transition-all after:duration-300 after:transform after:-translate-x-1/2 text-[#1E1E1E] after:bg-[#1E1E1E] hover:after:w-4/5`}
                    onClick={() => {
                      if (link.to === "contactus") {
                        toggledropdown("contactus");
                      }
                    }}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}

              <motion.div
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.3 }}
              >
                <LanguageSelector />
              </motion.div>
            </div>

            <div className="md:hidden flex items-center gap-3">
              <LanguageSelector />
              {!isDropdownOpen ? (
                <button
                  className="block md:hidden text-[20px] p-2 focus:outline-none"
                  onClick={() => toggledropdown("menu")}
                >
                  <CiMenuFries size={"2rem"} className={"text-black"} />
                </button>
              ) : (
                <button
                  className="block md:hidden text-[20px] p-2 focus:outline-none"
                  onClick={handleCloseDropdown}
                >
                  <TfiClose
                    className={"text-black text-[24px] md:text-[30px]"}
                  />
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </nav>
      <motion.div
        initial={{ y: "-100%", opacity: 0 }}
        animate={{
          y: isDropdownOpen ? 0 : "-100%",
          opacity: isDropdownOpen ? 1 : 0,
        }}
        transition={{ duration: 0.5 }}
        className={`fixed top-[64px] left-0 z-[230] w-full h-full overflow-y-auto bg-[#FFFFFF] shadow-lg rounded-br-2xl rounded-bl-2xl py-2 sm:py-10`}
      >
        <button
          className={`absolute top-3 right-4 hidden ${
            dropdownContent === "contactus" ? "md:block" : "md:hidden"
          } text-[20px] p-2 focus:outline-none`}
          onClick={handleCloseDropdown}
        >
          <TfiClose
            className={
              "text-black text-[24px] hover:text-red-500 hover:font-semibold"
            }
          />
        </button>

        <div className="w-full h-[90vh] overflow-auto sm:h-[500px] flex justify-center">
          {dropdownContent === "contactus" ? (
            <div className="w-full lg:w-1/2">
              <ConatctUs
                handleCloseDropdown={handleCloseDropdown}
                setIsDropdownOpen={setIsDropdownOpen}
              />
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col items-center space-y-6"
            >
              {navLinks.map((link, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.3 }}
                >
                  <Link
                    to={link.to !== "contactus" && link.to}
                    className={`pb-1 text-[16px] leading-[16.94px] relative after:content-[''] after:absolute after:left-1/2 after:bottom-0 after:w-0 after:h-[2px] ${
                      currentpath === link.to ? "after:w-4/5" : ""
                    } after:transition-all after:duration-300 after:transform after:-translate-x-1/2 hover:after:w-4/5`}
                    onClick={() => {
                      if (link.to === "contactus") {
                        toggledropdown("contactus");
                      } else {
                        setIsDropdownOpen(false);
                      }
                    }}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </motion.div>

      {isDropdownOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[100]"
          onClick={handleCloseDropdown}
        ></div>
      )}
    </>
  );
};

export default Navbar;
